import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { LANDING } from "@/constants/testIds";

// Collapsible FAQ replaces every long-form section on the landing page.
// Kept intentionally short — 6 questions is enough for a marketplace MVP
// without overwhelming the visitor.
const QUESTIONS = [
  {
    q: "How do I book a coach?",
    a: "Browse verified coaches on the marketplace, pick a time from their calendar, and pay for a single session at their listed price. No packs, no subscriptions.",
  },
  {
    q: "How much does a session cost?",
    a: "Each coach sets their own per-session price. You'll see the exact amount before you book — that's the price you pay, nothing else.",
  },
  {
    q: "Are coaches vetted?",
    a: "Yes. Every coach on Coachly is manually reviewed by our team and has to complete Stripe identity verification before their profile goes live.",
  },
  {
    q: "What if I need to cancel?",
    a: "You can cancel any booking up to 24 hours before the session for a full refund. Cancellations closer to the session may be partially refundable — details on your booking page.",
  },
  {
    q: "How do coaches get paid?",
    a: "Coaches connect their bank via Stripe Connect. We hold the payment until the session is marked complete, then release the coach's share automatically — minus a small platform fee.",
  },
  {
    q: "Do you offer refunds if I'm not happy?",
    a: "Yes. If your first session with a new coach doesn't work out, we'll refund it — no questions asked. Just message support within 48 hours.",
  },
];

export default function FAQ() {
  return (
    <section
      id="faq"
      data-testid={LANDING.faq}
      className="py-24 md:py-32 border-t border-slate-100"
    >
      <div className="max-w-[840px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12 md:mb-16"
        >
          <div className="eyebrow">§ Questions</div>
          <h2 className="mt-4 font-display font-medium text-[36px] sm:text-[44px] md:text-[52px] leading-[1.05] tracking-[-0.035em] text-slate-900">
            Everything you need to know.
          </h2>
        </motion.div>

        <Accordion type="single" collapsible className="w-full space-y-2">
          {QUESTIONS.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`item-${i}`}
              data-testid={LANDING.faqItem(i)}
              className="border-b border-slate-200 last:border-b-0"
            >
              <AccordionTrigger className="text-left font-display text-lg md:text-xl tracking-tight text-slate-900 hover:no-underline py-6">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-[15px] leading-relaxed pb-6 pr-8">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
